<script setup lang="ts">
import { computed, ref } from 'vue';

import StarRating from '@/components/ProductReviews/StarRating.vue';
import Image from '@/shared/components/UI/Image.vue';
import UserHeader from '@/shared/components/UI/UserHeader.vue';
import { ProductReview } from '@/shared/composables/productReviews/useProductReviews';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useProductStore } from '@/shared/pinia/modules/product';
import { dateString } from '@/shared/utils/dates';
import { trackUserEvent } from '@/shared/utils/tracking';

const { review } = defineProps<{
  review: ProductReview;
}>();

const { t } = useTranslations();
const productStore = useProductStore();

const commentMaxLength = 200;
const open = ref(false);

const publishedAtDate = computed(() =>
  dateString(review.published_at, {
    month: 'long',
    day: '2-digit',
    year: 'numeric'
  })
);

const toggle = () => {
  open.value = !open.value;
};

const title = computed(() => review.translated?.title ?? review.original.title);
const comment = computed(
  () => review.translated?.body_text ?? review.original.body_text ?? ''
);
const showTranslationButton = computed(
  () =>
    !!review.translated &&
    review.translated?.language !== review.original?.language
);

const onTranslateClicked = () => {
  showOriginal.value = true;
  if (!productStore.productId || !productStore.productName) {
    return;
  }

  trackUserEvent('show_original_review', {
    action: 'show_original_review',
    category: 'Product Review',
    item_id: `gid://shopify/Product/${productStore.productId}`,
    item_name: productStore.productName,
    label: productStore.productName,
    value: review.rating
  });
};

const showOriginal = ref(false);
</script>

<template>
  <article
    class="flex flex-col justify-between gap-4 pt-6 md:flex-row md:items-start md:pt-8"
  >
    <UserHeader class="md:shrink-0 md:basis-72">
      <template #avatar>
        <Image
          :src="review.author.avatar_url"
          :alt="review.author.display_name"
          class="size-full"
          img-classes="size-full rounded-full object-cover"
        />
      </template>
      <template #header>
        <span class="text-base font-bold">{{
          review.author.display_name
        }}</span>
      </template>
      <template #subheader>
        <time class="text-xs" :datetime="review.published_at">
          {{ publishedAtDate }}
        </time>
      </template>
    </UserHeader>

    <div class="flex flex-col gap-2 md:grow">
      <StarRating :rating="review.rating" class="mb-1"></StarRating>
      <div class="text-base font-bold">{{ title }}</div>
      <div>
        <div class="group flex flex-col">
          <div
            :id="`review-description-${review.id}`"
            class="prose max-w-none leading-normal"
          >
            <template v-if="comment.length <= commentMaxLength || open">
              {{ comment }}
            </template>
            <template v-else>
              {{ comment.substring(0, commentMaxLength) + '...' }}
              <button
                v-if="comment.length > commentMaxLength && !open"
                class="inline-flex items-center self-start font-bold underline"
                :aria-expanded="open ? true : false"
                :aria-controls="`review-description-${review.id}`"
                @click="toggle"
              >
                {{ t('global.read_more') }}
              </button>
            </template>

            <div v-if="showOriginal" class="">
              <div class="mt-4 text-xs text-gray-500">
                {{ t('reviews.original') }}:
              </div>
              <div class="mt-2 inline-flex rounded-md bg-gray-100 px-4 py-3">
                <div class="flex flex-col gap-1 text-gray-900">
                  <div class="font-bold">{{ review.original.title }}</div>
                  <div>
                    {{ review.original.body_text }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="showTranslationButton && !showOriginal"
            class="mt-3 text-xs text-gray-500"
          >
            <button
              class="underline hover:text-gray-600"
              :aria-label="t('reviews.view_original')"
              @click="onTranslateClicked"
            >
              {{ t('reviews.view_original') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
